import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

// Расширяем стандартный тип Event для beforeinstallprompt
interface BeforeInstallPromptEvent extends Event {
	readonly platforms: string[];
	readonly userChoice: Promise<{
		outcome: "accepted" | "dismissed";
		platform: string;
	}>;
	prompt(): Promise<void>;
}

type Store = State & Actions;

interface State {
	installPrompt: BeforeInstallPromptEvent | null;
}

interface Actions {
	setInstallPrompt: (event: BeforeInstallPromptEvent) => void;
}

/**
 * Хранилище для управления состоянием установки PWA.
 * Сохраняет событие beforeinstallprompt для последующего вызова.
 */
const usePwaInstallStore = create<Store>()(
	immer((set) => ({
		installPrompt: null,
		setInstallPrompt: (event) =>
			set((state) => {
				state.installPrompt = event;
			})
	}))
);

/**
 * Хук для доступа к состоянию PWA установки.
 * @returns {[BeforeInstallPromptEvent | null, (event: BeforeInstallPromptEvent | null) => void]} Кортеж с событием и функцией для его установки.
 */
export const usePwaInstallPrompt = (): [
	BeforeInstallPromptEvent | null,
	(event: BeforeInstallPromptEvent | null) => void
] => {
	const prompt = usePwaInstallStore((state) => state.installPrompt);
	const setPrompt = usePwaInstallStore((state) => state.setInstallPrompt);

	// Обернем set, чтобы можно было передавать null для очистки
	const set = (event: BeforeInstallPromptEvent | null) => {
		if (event) {
			setPrompt(event);
		} else {
			// Для сброса состояния используем setState напрямую, если потребуется
			usePwaInstallStore.setState({ installPrompt: null });
		}
	};

	return [prompt, set];
};

/**
 * Настраивает глобальный слушатель для события beforeinstallprompt.
 * Должен быть вызван один раз при инициализации приложения.
 */
export const setupPwaInstallListener = () => {
	window.addEventListener("beforeinstallprompt", (e) => {
		// Предотвращаем стандартное поведение браузера (показ мини-инфобара)
		e.preventDefault();
		const promptEvent = e as BeforeInstallPromptEvent;
		log.debug("PWA Global beforeinstallprompt event caught and stored");
		// Сохраняем событие в нашем Zustand store
		usePwaInstallStore.getState().setInstallPrompt(promptEvent);
	});
};
